import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'interview/i18n'
import {
  authenticateSession,
  connectToSession,
  initCandidateSession,
  disconnectSession,
  toggleReconnectingState,
  toggleRecordingState,
  updateConnectionCount,
  updateArchiveFromEvent,
} from 'actions/opentok'
import TimedInterviewError from 'interview/components/sequenced-interview/timed-interview-error'
import { updateError } from 'interview/actions/sequenced-interview'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { Subtitle } from 'components/styled/typography'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({
      errors: PropTypes.shape({}).isRequired,
    }).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  apiKey: PropTypes.string,
  session: PropTypes.shape({}),
  sessionId: PropTypes.string,
  token: PropTypes.string,
  testing: PropTypes.bool,
  candidateUuid: PropTypes.string,
  reconnecting: PropTypes.bool.isRequired,
  errorType: PropTypes.string,
  connectionCount: PropTypes.number.isRequired,
  archive: PropTypes.shape({}),
}

const defaultProps = {
  apiKey: '',
  sessionId: '',
  token: '',
  session: undefined,
  testing: false,
  candidateUuid: undefined,
  errorType: '',
  archive: undefined,
}

const mapStateToProps = state => ({
  apiKey: state.global.opentok.apiKey,
  sessionId: state.global.opentok.candidateSessionData.sessionId,
  token: state.global.opentok.candidateSessionData.token,
  archive: state.global.opentok.archive,
  session: state.global.opentok.session,
  reconnecting: state.global.opentok.reconnecting,
  errorType: state.interview.sequencedInterview.errorType,
  connectionCount: state.global.opentok.connectionCount,
})

export class Session extends Component {
  constructor(props) {
    super(props)

    this.state = {
      reconnectSuccess: false,
      onIe: false,
      iosUnsupported: false,
    }
  }

  componentDidMount() {
    const {
      sessionId,
      apiKey,
      token,
      dispatch,
      session,
      testing,
      candidateUuid,
      errorType,
    } = this.props
    const { iosUnsupported, onIe } = this.state

    this.detectDeviceAndBrowser()

    if (!errorType && !iosUnsupported && !onIe) {
      if (session && session.currentState !== 'connected') {
        this.connectToSession()
      } else if (sessionId && apiKey && token) {
        const candidateSessionData = { apiKey, sessionId, token }
        dispatch(initCandidateSession(candidateSessionData))
      } else if (!session && !testing) {
        dispatch(authenticateSession(candidateUuid))
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      sessionId,
      apiKey,
      token,
      dispatch,
      session,
      errorType,
      testing,
      connectionCount,
      reconnecting,
    } = this.props
    const { iosUnsupported, onIe } = this.state
    const updateSession = session && (session !== prevProps.session || session.currentState !== 'connected')
    if (sessionId !== prevProps.sessionId && token !== prevProps.token) {
      const candidateSessionData = { apiKey, sessionId, token }
      dispatch(initCandidateSession(candidateSessionData))
    }
    const hasError = errorType || iosUnsupported || onIe

    if (session && connectionCount !== prevProps.connectionCount && connectionCount > 0 && !testing && !hasError) {
      session.on({
        connectionCreated: (event) => {
          dispatch(updateConnectionCount(connectionCount + 1))
        },
        connectionDestroyed: (event) => {
          if (connectionCount > 0) {
            dispatch(updateConnectionCount(connectionCount - 1))
          }
        },
      })
    } else if (updateSession && !hasError) {
      if (connectionCount === 0) {
        session.on({
          connectionCreated: (event) => {
            dispatch(updateConnectionCount(connectionCount + 1))
          },
        })

        this.connectToSession()
      }

      session.on({
        archiveStarted: (event) => {
          if (event.archive) {
            dispatch(updateArchiveFromEvent(event.archive))
          }
          dispatch(toggleRecordingState(true))
        },
        archiveStopped: (event) => {
          if (event.archive) {
            dispatch(updateArchiveFromEvent(event.archive))
          }
          dispatch(toggleRecordingState(false))
        },
        sessionReconnecting: () => {
          this.setState({ reconnectSuccess: false })
          dispatch(toggleReconnectingState(true))
        },
        sessionReconnected: () => {
          dispatch(toggleReconnectingState(false))
          // show reconnection success message for 5 secs when reconnected
          this.setState({ reconnectSuccess: true })
          this.timeout = setTimeout(() => {
            this.setState({ reconnectSuccess: false })
          }, 5000)
        },
        sessionDisconnected: (event) => {
          if (reconnecting) {
            dispatch(toggleReconnectingState(false))
          }
          if (event.reason === 'networkDisconnected') {
            dispatch(updateError('networkError'))
          }
        },
      })
    }
  }

  componentWillUnmount() {
    const { dispatch, session } = this.props
    if (session && session.currentState === 'connected') {
      dispatch(disconnectSession())
    }

    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  connectToSession() {
    const { dispatch } = this.props
    dispatch(connectToSession())
  }

  detectDeviceAndBrowser() {
    const { dispatch } = this.props
    const { userAgent } = navigator

    if (userAgent.match(/msie|trident/i)) {
      this.setState({ onIe: true })
      dispatch(updateError('unsupportedBrowser'))
    } else if (userAgent.match(/crios|fxios/i)) {
      this.setState({ iosUnsupported: true })
      dispatch(updateError('unsupportedBrowser'))
    } else {
      dispatch(updateError(''))
    }
  }

  render() {
    const { i18n, reconnecting, testing } = this.props
    const { onIe, iosUnsupported, reconnectSuccess } = this.state

    if (reconnecting) {
      return (
        <>
          <Subtitle color="error110">{i18n.sequencedInterview.errors.reconnecting}</Subtitle>
          <LoadingIndicator />
        </>
      )
    }
    if (reconnectSuccess) {
      return (
        <Subtitle color="success110">
          {i18n.sequencedInterview.reconnectSuccess}
        </Subtitle>
      )
    }
    return (
      <TimedInterviewError
        testing={testing}
        onIe={onIe}
        iosUnsupported={iosUnsupported}
      />
    )
  }
}

Session.propTypes = propTypes
Session.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(Session)
